import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import {
  Package, AlertTriangle, Plus, Edit2, Save, X, MinusCircle, PlusCircle,
  Trash2, Warehouse, Search, Sun, Zap, Battery, Cable, Layers, Boxes,
  TrendingDown, DollarSign, Wrench, History, ArrowUp, ArrowDown, User,
} from "lucide-react";
import KpiCard, { KpiGrid } from "@/components/KpiCard";

/**
 * Admin Materials & Inventory module — industry-standard dashboard.
 *
 * Layout mirrors Zoho/Katana:
 *   - KPI strip (SKUs, stock value, low stock, out of stock)
 *   - Category chips with count + $ value per category
 *   - Status filter + search
 *   - Table (desktop) / card list (mobile) with stock-health bar
 */
const CATEGORY_META = {
  panel:    { label: "Panels",    icon: Sun,      className: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
  inverter: { label: "Inverters", icon: Zap,      className: "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300" },
  battery:  { label: "Batteries", icon: Battery,  className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
  wiring:   { label: "Wiring",    icon: Cable,    className: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300" },
  mounting: { label: "Mounting",  icon: Layers,   className: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" },
  general:  { label: "General",   icon: Wrench,   className: "bg-[#0A2540]/10 text-[#0A2540] dark:bg-slate-700/40 dark:text-slate-200" },
};
const CATEGORIES = Object.keys(CATEGORY_META);
const STATUS_OPTS = [
  { key: "all",   label: "All items" },
  { key: "low",   label: "Low stock" },
  { key: "out",   label: "Out of stock" },
  { key: "ok",    label: "In stock" },
];

function stockHealth(m) {
  const stock  = Number(m.stock) || 0;
  const reorder = Number(m.reorder_level) || 0;
  if (stock <= 0) return "out";
  if (stock <= reorder) return "low";
  return "ok";
}

function fmtMoney(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 10_000)    return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toFixed(2)}`;
}

export default function AdminMaterials() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/materials");
      setRows(r.data);
    } catch (e) {
      console.warn("materials fetch:", e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // ---------- Derived stats ----------
  const stats = useMemo(() => {
    const acc = {
      total_skus: rows.length,
      stock_value: 0,
      low: 0,
      out: 0,
    };
    for (const m of rows) {
      const stock = Number(m.stock) || 0;
      const cost  = Number(m.cost_usd) || 0;
      acc.stock_value += stock * cost;
      const h = stockHealth(m);
      if (h === "low") acc.low += 1;
      if (h === "out") acc.out += 1;
    }
    return acc;
  }, [rows]);

  const byCategory = useMemo(() => {
    const g = {};
    for (const c of CATEGORIES) g[c] = { skus: 0, value: 0, low: 0 };
    for (const m of rows) {
      const c = CATEGORIES.includes(m.category) ? m.category : "general";
      g[c].skus += 1;
      g[c].value += (Number(m.stock) || 0) * (Number(m.cost_usd) || 0);
      if (stockHealth(m) !== "ok") g[c].low += 1;
    }
    return g;
  }, [rows]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((m) => {
      if (category !== "all" && (m.category || "general") !== category) return false;
      if (statusFilter !== "all") {
        const h = stockHealth(m);
        if (statusFilter === "low" && h !== "low") return false;
        if (statusFilter === "out" && h !== "out") return false;
        if (statusFilter === "ok"  && h !== "ok")  return false;
      }
      if (query) {
        const hay = `${m.name || ""} ${m.sku || ""} ${m.vendor || ""} ${m.category || ""}`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [rows, q, category, statusFilter]);

  return (
    <div data-testid="admin-materials">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="overline">Inventory</p>
          <h1 className="text-3xl sm:text-4xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Materials</h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
            {stats.total_skus} SKU{stats.total_skus !== 1 && "s"} tracked
          </p>
        </div>
        <button onClick={() => setEditing({})} className="btn-accent text-sm inline-flex items-center" data-testid="mat-new-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New material
        </button>
      </div>

      {/* KPI tiles */}
      <KpiGrid className="mb-6">
        <KpiCard testId="kpi-skus" label="Total SKUs" value={stats.total_skus} tone="neutral" icon={Boxes} />
        <KpiCard testId="kpi-value" label="Stock value" value={stats.stock_value} money decimals={0} tone="neutral" icon={DollarSign} />
        <KpiCard testId="kpi-low" label="Low stock" value={stats.low} tone={stats.low > 0 ? "attention" : "positive"} icon={TrendingDown} />
        <KpiCard testId="kpi-out" label="Out of stock" value={stats.out} tone={stats.out > 0 ? "critical" : "positive"} icon={AlertTriangle} />
      </KpiGrid>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Category filter">
        <CategoryChip
          testId="cat-chip-all"
          active={category === "all"}
          onClick={() => setCategory("all")}
          label="All"
          count={stats.total_skus}
          value={stats.stock_value}
        />
        {CATEGORIES.map((c) => {
          const meta = CATEGORY_META[c];
          const s = byCategory[c] || { skus: 0, value: 0, low: 0 };
          if (s.skus === 0 && category !== c) return null;   // hide empty chips
          return (
            <CategoryChip
              key={c}
              testId={`cat-chip-${c}`}
              active={category === c}
              onClick={() => setCategory(c)}
              icon={meta.icon}
              label={meta.label}
              count={s.skus}
              value={s.value}
              lowCount={s.low}
            />
          );
        })}
      </div>

      {/* Search + status filter */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, SKU, vendor…"
            data-testid="mat-search"
            className="w-full pl-9 pr-3 h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-md bg-white dark:bg-slate-900 text-sm outline-none focus:ring-2 focus:ring-[#C9A356]/40"
          />
        </div>
        <div className="inline-flex rounded-lg border border-[#E2E8F0] dark:border-slate-700 overflow-hidden">
          {STATUS_OPTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              data-testid={`mat-status-${s.key}`}
              className={`px-3 h-10 text-xs font-semibold transition ${
                statusFilter === s.key
                  ? "bg-[#0A2540] text-white"
                  : "bg-white dark:bg-slate-900 text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card-pb overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-[#475569]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="mat-empty">
            <Package className="w-10 h-10 text-[#E2E8F0] mx-auto mb-2" />
            <div className="text-sm text-[#475569]">
              {rows.length === 0 ? "No materials tracked yet." : "No materials match this filter."}
            </div>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <table className="w-full text-sm hidden md:table">
              <thead className="bg-slate-50 dark:bg-slate-900 text-left">
                <tr className="text-[11px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
                  <th className="px-4 py-3">Item</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3 text-left">Stock health</th>
                  <th className="px-4 py-3 text-right">Cost</th>
                  <th className="px-4 py-3 text-right">Value</th>
                  <th className="px-4 py-3">Warehouse</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <MaterialRow
                    key={m.material_id}
                    m={m}
                    onAdjust={setAdjustFor}
                    onEdit={setEditing}
                    onHistory={setHistoryFor}
                  />
                ))}
              </tbody>
            </table>
            {/* Mobile cards */}
            <ul className="md:hidden divide-y divide-[#E2E8F0] dark:divide-slate-800" data-testid="mat-mobile-list">
              {filtered.map((m) => (
                <MaterialCard
                  key={m.material_id}
                  m={m}
                  onAdjust={setAdjustFor}
                  onEdit={setEditing}
                  onHistory={setHistoryFor}
                />
              ))}
            </ul>
          </>
        )}
      </div>

      {editing && <EditModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {adjustFor && <AdjustModal material={adjustFor} onClose={() => setAdjustFor(null)} onSaved={() => { setAdjustFor(null); load(); }} />}
      {historyFor && <MovementDrawer material={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
}

// ---------- Small components ----------

function KpiTile({ label, value, icon: Icon, accent = "muted", testId }) {
  const tone = {
    navy:   "bg-[#0A2540] text-white",
    gold:   "bg-[#C9A356]/15 text-[#C9A356] dark:bg-[#C9A356]/20",
    amber:  "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
    red:    "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
    muted:  "bg-slate-100 text-[#475569] dark:bg-slate-800 dark:text-slate-400",
  }[accent];
  return (
    <div className="card-pb p-4 flex items-center gap-3" data-testid={testId}>
      <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${tone}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[#94A3B8]">{label}</div>
        <div className="font-heading text-xl sm:text-2xl font-bold text-[#0A2540] dark:text-white tabular-nums truncate">
          {value}
        </div>
      </div>
    </div>
  );
}

function CategoryChip({ active, onClick, icon: Icon, label, count, value, lowCount, testId }) {
  return (
    <button
      onClick={onClick}
      role="tab"
      aria-selected={active}
      data-testid={testId}
      className={`inline-flex items-center gap-2 h-10 pl-3 pr-3 rounded-full border transition text-[13px] font-semibold ${
        active
          ? "bg-[#0A2540] text-white border-[#0A2540]"
          : "bg-white dark:bg-slate-900 text-[#475569] dark:text-slate-300 border-[#E2E8F0] dark:border-slate-700 hover:border-[#0A2540] dark:hover:border-white"
      }`}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span>{label}</span>
      <span className={`text-[11px] rounded-full px-1.5 ${active ? "bg-white/20" : "bg-slate-100 dark:bg-slate-800"}`}>
        {count}
      </span>
      {value !== undefined && (
        <span className={`text-[11px] tabular-nums ${active ? "text-white/70" : "text-[#94A3B8]"}`}>
          {fmtMoney(value)}
        </span>
      )}
      {lowCount > 0 && (
        <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-white" : "bg-amber-500"}`} title={`${lowCount} needs attention`} />
      )}
    </button>
  );
}

function HealthBar({ m }) {
  const stock = Number(m.stock) || 0;
  const reorder = Number(m.reorder_level) || 0;
  const target = Math.max(reorder * 2, stock, 1);
  const pct = Math.min(100, Math.round((stock / target) * 100));
  const h = stockHealth(m);
  const colorClass = h === "out" ? "bg-red-500" : h === "low" ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="min-w-[120px]">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className={`font-semibold tabular-nums ${h === "out" ? "text-red-600 dark:text-red-400" : h === "low" ? "text-amber-600 dark:text-amber-400" : "text-[#0A2540] dark:text-white"}`}>
          {stock} {m.unit}
        </span>
        <span className="text-[10px] text-[#94A3B8]">reorder at {reorder}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div className={`h-full ${colorClass} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CategoryPill({ category }) {
  const meta = CATEGORY_META[category] || CATEGORY_META.general;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${meta.className}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function MaterialRow({ m, onAdjust, onEdit, onHistory }) {
  const h = stockHealth(m);
  const rowTone = h === "out" ? "bg-red-50/40 dark:bg-red-900/10" : h === "low" ? "bg-amber-50/40 dark:bg-amber-900/10" : "";
  const value = (Number(m.stock) || 0) * (Number(m.cost_usd) || 0);
  return (
    <tr className={`border-t border-[#E2E8F0] dark:border-slate-800 ${rowTone}`} data-testid={`mat-row-${m.material_id}`}>
      <td className="px-4 py-3">
        <div className="font-medium text-[#0A2540] dark:text-white">{m.name}</div>
        <div className="text-[11px] font-mono text-[#475569] dark:text-slate-400 mt-0.5">{m.sku || "no SKU"}{m.vendor ? ` · ${m.vendor}` : ""}</div>
      </td>
      <td className="px-4 py-3"><CategoryPill category={m.category || "general"} /></td>
      <td className="px-4 py-3"><HealthBar m={m} /></td>
      <td className="px-4 py-3 text-right tabular-nums text-[#475569] dark:text-slate-400">${(Number(m.cost_usd) || 0).toFixed(2)}</td>
      <td className="px-4 py-3 text-right tabular-nums font-medium text-[#0A2540] dark:text-white">{fmtMoney(value)}</td>
      <td className="px-4 py-3 text-[#475569] dark:text-slate-400"><span className="inline-flex items-center gap-1"><Warehouse className="w-3 h-3" /> {m.warehouse || "Main"}</span></td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button onClick={() => onAdjust(m)} className="text-[#C9A356] hover:underline text-xs mr-3" data-testid={`mat-adjust-${m.material_id}`}>Adjust</button>
        <button onClick={() => onHistory(m)} aria-label="Movement log" title="Movement log" className="text-[#475569] hover:text-[#0A2540] dark:hover:text-white mr-2" data-testid={`mat-history-${m.material_id}`}><History className="w-3.5 h-3.5 inline" /></button>
        <button onClick={() => onEdit(m)} aria-label="Edit material" className="text-[#475569] hover:text-[#0A2540] dark:hover:text-white" data-testid={`mat-edit-${m.material_id}`}><Edit2 className="w-3.5 h-3.5" /></button>
      </td>
    </tr>
  );
}

function MaterialCard({ m, onAdjust, onEdit, onHistory }) {
  const value = (Number(m.stock) || 0) * (Number(m.cost_usd) || 0);
  return (
    <li className="p-4" data-testid={`mat-card-${m.material_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-[#0A2540] dark:text-white">{m.name}</div>
          <div className="text-[11px] font-mono text-[#475569] dark:text-slate-400 mt-0.5 truncate">{m.sku || "no SKU"}{m.vendor ? ` · ${m.vendor}` : ""}</div>
          <div className="mt-1"><CategoryPill category={m.category || "general"} /></div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase text-[#94A3B8]">Value</div>
          <div className="font-semibold tabular-nums text-[#0A2540] dark:text-white">{fmtMoney(value)}</div>
        </div>
      </div>
      <div className="mt-3"><HealthBar m={m} /></div>
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={() => onAdjust(m)} className="px-3 h-8 rounded-md border border-[#C9A356]/40 text-[#C9A356] text-xs font-semibold" data-testid={`mat-adjust-card-${m.material_id}`}>Adjust</button>
        <button onClick={() => onHistory(m)} className="px-3 h-8 rounded-md border border-[#E2E8F0] dark:border-slate-700 text-[#475569] dark:text-slate-300 text-xs font-semibold inline-flex items-center gap-1" data-testid={`mat-history-card-${m.material_id}`}>
          <History className="w-3.5 h-3.5" /> History
        </button>
        <button onClick={() => onEdit(m)} className="px-3 h-8 rounded-md border border-[#E2E8F0] dark:border-slate-700 text-[#475569] dark:text-slate-300 text-xs font-semibold" data-testid={`mat-edit-card-${m.material_id}`}>Edit</button>
      </div>
    </li>
  );
}

// ---------- Movement Log drawer ----------
function MovementDrawer({ material, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/materials/${material.material_id}/movements`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ movements: [] }); });
    return () => { cancelled = true; };
  }, [material.material_id]);

  const movements = data?.movements || [];

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/50 dark:bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true"
      data-testid="movement-drawer"
    >
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right">
        <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-[#E2E8F0] dark:border-slate-700 p-4 sm:p-5 flex items-start justify-between gap-3">
          <div>
            <p className="overline inline-flex items-center gap-1"><History className="w-3 h-3" /> Stock history</p>
            <h2 className="font-heading text-lg sm:text-xl font-medium text-[#0A2540] dark:text-white mt-1">
              {material.name}
            </h2>
            <p className="text-xs text-[#475569] dark:text-slate-400">
              Current stock: <span className="font-mono text-[#0A2540] dark:text-white">{material.stock} {material.unit}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="movement-drawer-close"
            className="w-9 h-9 grid place-items-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-[#475569] dark:text-slate-400"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="p-4 sm:p-5">
          {data === null ? (
            <p className="text-sm text-[#475569] dark:text-slate-400">Loading movement history…</p>
          ) : movements.length === 0 ? (
            <div className="text-center py-10">
              <History className="w-10 h-10 text-[#E2E8F0] mx-auto mb-2" />
              <p className="text-sm text-[#475569] dark:text-slate-400">No movements recorded yet.</p>
              <p className="text-xs text-[#94A3B8] mt-1">Stock adjustments and PO receipts will appear here.</p>
            </div>
          ) : (
            <ol className="relative border-l border-[#E2E8F0] dark:border-slate-700 pl-6 space-y-4" data-testid="movement-list">
              {movements.map((m) => {
                const delta = Number(m.delta) || 0;
                const positive = delta > 0;
                return (
                  <li key={m.movement_id} className="relative" data-testid={`movement-${m.movement_id}`}>
                    <span className={`absolute -left-[29px] top-1 w-5 h-5 rounded-full grid place-items-center text-white shrink-0 ${positive ? "bg-emerald-500" : "bg-red-500"}`}>
                      {positive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    </span>
                    <div className="rounded-md border border-[#E2E8F0] dark:border-slate-700 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-[#0A2540] dark:text-white">
                            {m.reason_label || m.reason || "Adjustment"}
                          </div>
                          <div className="text-[11px] text-[#475569] dark:text-slate-400 mt-0.5 inline-flex items-center gap-1">
                            <User className="w-3 h-3" /> {m.by_name || "System"} · {formatMovementTime(m.at)}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`text-lg font-bold tabular-nums ${positive ? "text-emerald-600" : "text-red-600"}`}>
                            {positive ? "+" : ""}{delta} {material.unit}
                          </div>
                          {m.new_stock !== undefined && m.new_stock !== null && (
                            <div className="text-[10px] text-[#94A3B8]">→ {m.new_stock} on hand</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function formatMovementTime(iso) {
  if (!iso) return "unknown time";
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = Math.floor((now - d.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "unknown time";
  }
}

function EditModal({ initial, onClose, onSaved }) {
  const isNew = !initial.material_id;
  const [form, setForm] = useState({
    name: initial.name || "", sku: initial.sku || "", unit: initial.unit || "ea",
    stock: initial.stock || 0, reorder_level: initial.reorder_level || 0,
    cost_usd: initial.cost_usd || 0, retail_usd: initial.retail_usd || 0, vendor: initial.vendor || "",
    warehouse: initial.warehouse || "Main", category: initial.category || "general",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post("/materials", form);
      else await api.patch(`/materials/${initial.material_id}`, form);
      toast.success(isNew ? "Material created" : "Updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!confirm(`Delete ${initial.name}? This cannot be undone.`)) return;
    setSaving(true);
    try { await api.delete(`/materials/${initial.material_id}`); toast.success("Deleted"); onSaved(); }
    catch { toast.error("Delete failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center px-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="mat-edit-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0] dark:border-slate-700">
          <h2 className="font-heading text-lg text-[#0A2540] dark:text-white">{isNew ? "New material" : `Edit ${initial.name}`}</h2>
          <button onClick={onClose} aria-label="Close" className="text-[#475569]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
          <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="mat-form-name" span={2} />
          <Field label="SKU" value={form.sku} onChange={(v) => setForm({ ...form, sku: v })} />
          <Field label="Unit (ea, kg, m, etc.)" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} />
          <Field label="In stock" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: parseFloat(v) || 0 })} testId="mat-form-stock" />
          <Field label="Reorder when below" type="number" value={form.reorder_level} onChange={(v) => setForm({ ...form, reorder_level: parseFloat(v) || 0 })} />
          <Field label="Cost per unit (USD) · admin only" type="number" value={form.cost_usd} onChange={(v) => setForm({ ...form, cost_usd: parseFloat(v) || 0 })} testId="mat-form-cost" />
          <Field label="Retail price (USD) · appears on invoice" type="number" value={form.retail_usd} onChange={(v) => setForm({ ...form, retail_usd: parseFloat(v) || 0 })} testId="mat-form-retail" />
          <Field label="Vendor" value={form.vendor} onChange={(v) => setForm({ ...form, vendor: v })} />
          <Field label="Warehouse" value={form.warehouse} onChange={(v) => setForm({ ...form, warehouse: v })} />
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#475569] font-medium mb-1 block">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between p-5 border-t border-[#E2E8F0] dark:border-slate-700">
          {!isNew ? (
            <button onClick={del} disabled={saving} className="text-rose-500 text-sm inline-flex items-center hover:underline disabled:opacity-50" data-testid="mat-form-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-outline-navy text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-accent text-sm" data-testid="mat-form-save">
              <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : isNew ? "Create" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdjustModal({ material, onClose, onSaved }) {
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("manual_adjustment");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!delta) { toast.error("Enter a non-zero delta"); return; }
    setSaving(true);
    try {
      await api.post(`/materials/${material.material_id}/adjust`, { delta, reason });
      toast.success("Stock updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong — please try again."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center px-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="mat-adjust-modal">
        <div className="p-5 border-b border-[#E2E8F0] dark:border-slate-700">
          <h2 className="font-heading text-lg text-[#0A2540] dark:text-white">Adjust {material.name}</h2>
          <div className="text-xs text-[#475569] mt-1">Current: <strong>{material.stock} {material.unit}</strong></div>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setDelta((d) => d - 1)} aria-label="Decrease" className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded"><MinusCircle className="w-5 h-5" /></button>
            <input type="number" value={delta} onChange={(e) => setDelta(parseFloat(e.target.value) || 0)}
                   data-testid="mat-adjust-delta"
                   className="flex-1 text-center text-2xl tabular-nums border border-[#E2E8F0] dark:border-slate-700 rounded-md py-2 bg-white dark:bg-slate-900" />
            <button onClick={() => setDelta((d) => d + 1)} aria-label="Increase" className="p-2 text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded"><PlusCircle className="w-5 h-5" /></button>
          </div>
          <div className="text-center text-sm text-[#475569]">New stock will be <strong>{material.stock + delta} {material.unit}</strong></div>
          <Field label="Reason" value={reason} onChange={setReason} />
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-[#E2E8F0] dark:border-slate-700">
          <button onClick={onClose} className="btn-outline-navy text-sm">Cancel</button>
          <button onClick={save} disabled={saving || !delta} className="btn-accent text-sm" data-testid="mat-adjust-save">Apply</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="text-[11px] uppercase tracking-wider text-[#475569] font-medium mb-1 block">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
             data-testid={testId}
             className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm outline-none focus:ring-2 focus:ring-[#C9A356]/40 focus:border-[#C9A356]" />
    </div>
  );
}
